import clsx from 'clsx'
import {cloneDeep, isEmpty, isEqual, omit} from 'lodash'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../../gori/components'
import {DEFAULT_PAGE, PAGE_SIZE_MAX} from '../../../../../gori/constants'
import {useCancelToken} from '../../../../../gori/hooks'
import {Button} from '../../../../../gori/widgets'
import {A1BatchService, FilterKey, SaveFilterModal} from '../../../a1'

type Props = {
  className?: string
  labelClassName?: string
  showModalSaveCurrentFilter: boolean
  setShowModalSaveCurrentFilter: any
  dataFilters: any
  setClickFilter: any
  handleClearAll: any
  filterKey: FilterKey
  paramsOptions: any
}

const SaveFilterButton: FC<Props> = ({
  showModalSaveCurrentFilter = false,
  setShowModalSaveCurrentFilter,
  dataFilters,
  className,
  labelClassName,
  setClickFilter,
  handleClearAll,
  filterKey,
  paramsOptions = [],
}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState<{delete: number | undefined}>({
    delete: undefined,
  })
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [isClearValue, setIsClearValue] = useState(false)
  const filterOrderRef = useRef<any>(null)
  const [valueSearch, setValueSearch] = useState('')
  const [saveFilters, setSaveFilters] = useState<{options: any; value: any}>({
    options: [],
    value: undefined,
  })
  useEffect(() => {
    const dataParams = paramsOptions.reduce((acc, param) => {
      const value = searchParams.get(param) || undefined
      if (value !== undefined) {
        acc[param] = value
      }
      return acc
    }, {})
    setSaveFilters((prev) => ({...prev, value: dataParams}))
  }, [paramsOptions, searchParams])

  const getSaveFilter = useCallback(async () => {
    try {
      const config = {
        params: {
          key: filterKey,
          page: DEFAULT_PAGE,
          page_size: PAGE_SIZE_MAX,
        },
        cancelToken: newCancelToken(),
      }

      const filters: any = await A1BatchService.getDataSaveFilter(config)

      if (!isEmpty(filters)) {
        const customsOption = filters.map((filter) => ({
          label: filter.name,
          value: JSON.parse(filter.payload),
          id: filter.id,
        }))
        setSaveFilters((prev) => ({...prev, options: customsOption}))
      } else {
        setSaveFilters((prev) => ({...prev, options: []}))
      }
    } catch (error) {
      if (isCancel(error)) return
    }
  }, [filterKey, isCancel, newCancelToken])

  useEffect(() => {
    getSaveFilter()
  }, [getSaveFilter])

  const findObjectByValue = useMemo(() => {
    return (arr, targetValue) => {
      for (const obj of arr) {
        if (isEqual(obj.value, targetValue)) {
          return obj
        }
      }
      return {label: undefined, value: undefined}
    }
  }, [])

  const objectValue = useMemo(() => {
    const found = findObjectByValue(saveFilters.options, saveFilters.value)
    if (found?.label) {
      setClickFilter(true)
      if (showModalSaveCurrentFilter) {
        toast.error(
          intl.formatMessage(
            {id: 'INPUT_ALREADY_EXISTS'},
            {input: intl.formatMessage({id: 'THE_CURRENT_FILTER'})}
          )
        )
        setShowModalSaveCurrentFilter((prev) => ({...prev, save_current_filter: false}))
      }
    }
    return found
  }, [
    findObjectByValue,
    intl,
    saveFilters.options,
    saveFilters.value,
    setClickFilter,
    setShowModalSaveCurrentFilter,
    showModalSaveCurrentFilter,
  ])

  const handleDropdownClick = () => {
    setVisibleMenu(!visibleMenu)
  }

  const resetDropdown = () => {
    setVisibleMenu(false)
  }

  const customsData = useMemo(() => {
    if (!valueSearch) return saveFilters.options
    return cloneDeep(saveFilters.options).filter(({label}) => {
      return label.toLowerCase().includes(valueSearch.toLowerCase().trim())
    })
  }, [saveFilters.options, valueSearch])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterOrderRef.current && !filterOrderRef.current.contains(event.target)) {
        resetDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const deleteSavedFilter = async (id) => {
    try {
      setLoading((prev) => ({...prev, delete: id}))
      const {message}: any = await A1BatchService.deleteDataSaveFilter(id, {
        cancelToken: newCancelToken(),
      })
      await getSaveFilter()
      await toast.success(message)
    } catch (error: any) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, delete: undefined}))
    }
  }

  const handleChangeFilter = (value) => {
    let params: any = {}
    for (let param of searchParams.entries()) {
      params[param[0]] = param[1]
    }
    params = {
      ...omit(params, paramsOptions),
      ...value,
    }

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) {
        delete params[key]
      }
    })
    setSearchParams(params)
  }

  const handleDeleteField = () => {
    setIsClearValue(true)
    handleClearAll()
    setTimeout(() => {
      resetDropdown()
      setIsClearValue(false)
    }, 1000)
  }

  const renderMenuItem = (data, index) => {
    const {label, value, id} = data
    return (
      <div key={index} className={clsx('dropdown-submenu', {'cursor-no-drop': loading.delete})}>
        <div
          className={clsx('dropdown-submenu__item', {
            'dropdown-submenu__item__active': isEqual(saveFilters.value, value),
            'pe-none': loading.delete,
          })}
        >
          <div
            className='flex-fill text-truncate'
            onClick={() => {
              resetDropdown()
              handleChangeFilter(value)
            }}
          >
            {label}
          </div>
          {loading.delete === id ? (
            <span className='spinner-border spinner-border-sm align-middle me-2' />
          ) : (
            <Button
              event={() => deleteSavedFilter(id)}
              className='text-hover-danger border-0 ms-2 bg-transparent rounded-circle bg-hover-white py-1'
            >
              <KTSVG
                path='/media/gori/common/delete.svg'
                className={clsx('opacity-50', {
                  'text-white': isEqual(saveFilters.value, value),
                })}
                svgClassName='mh-10px'
              />
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <SaveFilterModal
        optionsCurrentFilter={saveFilters.options}
        show={!objectValue?.label && showModalSaveCurrentFilter}
        nameHeader={intl.formatMessage({id: 'SAVE_FILTER'})}
        dataFilters={dataFilters}
        handleClose={() =>
          setShowModalSaveCurrentFilter((prev) => ({...prev, save_current_filter: false}))
        }
        handleUpdateFilter={getSaveFilter}
        filterKey={filterKey}
      />
      <div
        className={clsx('dropdown min-w-200px', className, {
          'border border-2 border-primary rounded-2': objectValue?.label,
        })}
        ref={filterOrderRef}
      >
        <div
          className={clsx(
            'dropdown__label btn btn-sm btn-active-light-primary border',
            labelClassName
          )}
          onClick={handleDropdownClick}
        >
          <div className='dropdown__label__left fw-bold'>
            <span className='fw-bolder fs-5 text-gray-600'>
              {intl.formatMessage({id: 'SAVED_FILTERS'})}
            </span>
          </div>
          {objectValue?.label ? (
            <>
              <span className='dropdown__label__value text-truncate'>{objectValue?.label}</span>
              <Button
                event={handleDeleteField}
                className='text-hover-danger border-0 ms-2 bg-transparent rounded-circle bg-hover-white py-1 rounded-circle bg-hover-white py-1'
              >
                <KTSVG
                  path='/media/gori/common/delete.svg'
                  className='opacity-50 m-0'
                  svgClassName='mh-10px'
                />
              </Button>
            </>
          ) : (
            <i className='bi bi-caret-down-fill ms-2'></i>
          )}
        </div>
        <div className={clsx('dropdown-menu dropdown__menu', {show: visibleMenu && !isClearValue})}>
          <div className={clsx('dropdown-submenu')}>
            <div className='px-3 py-2 sticky-top bg-white'>
              <input
                type='text'
                autoComplete='off'
                className='form-control'
                onChange={(e) => {
                  setValueSearch(e.target.value)
                }}
                value={valueSearch}
                autoFocus
              />
            </div>
            {!isEmpty(customsData) && customsData.map((item, index) => renderMenuItem(item, index))}
          </div>
        </div>
      </div>
    </>
  )
}

export {SaveFilterButton}
